import { Info } from '@mui/icons-material';
import { Grid, Typography } from '@mui/material';
import { getArrearsComiteeReport } from 'lib/api/collections';
import { ArrearsComiteeDocument } from 'lib/api/collections/types';
import { SelectInput, TextInput } from 'lib/common/Inputs';
import { DocumentProfileContext } from 'lib/context/DocumentProfileProvider';
import TableCard from 'lib/designSystem/Card/TableCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import CustomToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import { showAlert } from 'lib/functions/utils';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

const toOptions = (options: string[]) => [
  { value: 'all', label: 'Todos' },
  ...options
    .filter(Boolean)
    .sort()
    .map((value) => ({ value, label: value })),
];

type FiltersProps = {
  selectedStatus: string;
  selectedArrearTrench: string;
  selectedCollectionState: string;
  selectedCollectionOption: string;
  selectedBusiness: string;
  selectedStakeholder: string;
  selectedCollector: string;
  selectedCommercialExecutive: string;
  folio: string;
  businessIdentifier: string;
  stakeholderIdentifier: string;
  setSelectedStatus: (v: string) => void;
  setSelectedArrearTrench: (v: string) => void;
  setSelectedCollectionState: (v: string) => void;
  setSelectedCollectionOption: (v: string) => void;
  setSelectedBusiness: (v: string) => void;
  setSelectedStakeholder: (v: string) => void;
  setSelectedCollector: (v: string) => void;
  setSelectedCommercialExecutive: (v: string) => void;
  setFolio: (v: string) => void;
  setBusinessIdentifier: (v: string) => void;
  setStakeholderIdentifier: (v: string) => void;
  collectionStatesOptions: string[];
  collectionOptionOptions: string[];
  collectorOptions: string[];
  commercialExecutiveOptions: string[];
  businessNameOptions: string[];
  stakeholderNameOptions: string[];
  arrearTrenchOptions: string[];
};

const Filters: React.FC<FiltersProps> = ({
  selectedStatus,
  selectedArrearTrench,
  selectedCollectionState,
  selectedCollectionOption,
  selectedBusiness,
  selectedStakeholder,
  selectedCollector,
  selectedCommercialExecutive,
  folio,
  businessIdentifier,
  stakeholderIdentifier,
  setSelectedStatus,
  setSelectedArrearTrench,
  setSelectedCollectionState,
  setSelectedCollectionOption,
  setSelectedBusiness,
  setSelectedStakeholder,
  setSelectedCollector,
  setSelectedCommercialExecutive,
  setFolio,
  setBusinessIdentifier,
  setStakeholderIdentifier,
  collectionStatesOptions,
  collectionOptionOptions,
  collectorOptions,
  commercialExecutiveOptions,
  businessNameOptions,
  stakeholderNameOptions,
  arrearTrenchOptions,
}) => {
  const statusOptions = [
    { value: 'all', label: 'Todos' },
    { value: 'Vigente', label: 'Vigente' },
    { value: 'En mora', label: 'En mora' },
  ];

  return (
    <TableCard sx={{ marginBottom: '16px' }}>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <SelectInput
            label="Estado"
            value={selectedStatus}
            onChange={(value) => setSelectedStatus(value || 'all')}
            options={statusOptions}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectInput
            label="Tramo vencimiento"
            value={selectedArrearTrench}
            onChange={(value) => setSelectedArrearTrench(value || 'all')}
            options={toOptions(arrearTrenchOptions)}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectInput
            label="E. Cobranza"
            value={selectedCollectionState}
            onChange={(value) => setSelectedCollectionState(value || 'all')}
            options={toOptions(collectionStatesOptions)}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectInput
            label="S. Cobranza"
            value={selectedCollectionOption}
            onChange={(value) => setSelectedCollectionOption(value || 'all')}
            options={toOptions(collectionOptionOptions)}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectInput
            label="Cliente"
            value={selectedBusiness}
            onChange={(value) => setSelectedBusiness(value || 'all')}
            options={toOptions(businessNameOptions)}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectInput
            label="Deudor"
            value={selectedStakeholder}
            onChange={(value) => setSelectedStakeholder(value || 'all')}
            options={toOptions(stakeholderNameOptions)}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectInput
            label="Ejecutivo cobranza"
            value={selectedCollector}
            onChange={(value) => setSelectedCollector(value || 'all')}
            options={toOptions(collectorOptions)}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectInput
            label="Ejecutivo comercial"
            value={selectedCommercialExecutive}
            onChange={(value) => setSelectedCommercialExecutive(value || 'all')}
            options={toOptions(commercialExecutiveOptions)}
          />
        </Grid>
        <Grid item xs={3}>
          <TextInput
            label="Rut Cliente"
            value={businessIdentifier || null}
            onChange={(value) => setBusinessIdentifier(value ?? '')}
          />
        </Grid>
        <Grid item xs={3}>
          <TextInput
            label="Rut Deudor"
            value={stakeholderIdentifier || null}
            onChange={(value) => setStakeholderIdentifier(value ?? '')}
          />
        </Grid>
        <Grid item xs={3}>
          <TextInput
            label="Folio"
            value={folio || null}
            onChange={(value) => setFolio(value ?? '')}
          />
        </Grid>
      </Grid>
    </TableCard>
  );
};

const groupByBusinessName = (
  documents: ArrearsComiteeDocument[],
): Record<string, ArrearsComiteeDocument[]> => {
  return documents.reduce(
    (acc, document) => {
      const businessName = document.businessName || 'Sin Cliente';
      if (!acc[businessName]) {
        acc[businessName] = [];
      }
      acc[businessName].push(document);
      return acc;
    },
    {} as Record<string, ArrearsComiteeDocument[]>,
  );
};

const ArrearsComitee: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ArrearsComiteeDocument[]>([]);

  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedArrearTrench, setSelectedArrearTrench] =
    useState<string>('all');
  const [selectedCollectionState, setSelectedCollectionState] =
    useState<string>('all');
  const [selectedCollectionOption, setSelectedCollectionOption] =
    useState<string>('all');
  const [selectedBusiness, setSelectedBusiness] = useState<string>('all');
  const [selectedStakeholder, setSelectedStakeholder] = useState<string>('all');
  const [selectedCollector, setSelectedCollector] = useState<string>('all');
  const [selectedCommercialExecutive, setSelectedCommercialExecutive] =
    useState<string>('all');
  const [folio, setFolio] = useState<string>('');
  const [businessIdentifier, setBusinessIdentifier] = useState<string>('');
  const [stakeholderIdentifier, setStakeholderIdentifier] =
    useState<string>('');
  const [collectionStatesOptions, setCollectionStatesOptions] = useState<
    string[]
  >([]);
  const [collectionOptionOptions, setCollectionOptionOptions] = useState<
    string[]
  >([]);
  const [collectorOptions, setCollectorOptions] = useState<string[]>([]);
  const [commercialExecutiveOptions, setCommercialExecutiveOptions] = useState<
    string[]
  >([]);
  const [businessNameOptions, setBusinessNameOptions] = useState<string[]>([]);
  const [stakeholderNameOptions, setStakeholderNameOptions] = useState<
    string[]
  >([]);
  const [arrearTrenchOptions, setArrearTrenchOptions] = useState<string[]>([]);

  const [showSectioned, setShowSectioned] = useState<boolean>(false);

  const { onOpenDocumentProfile } = useContext(DocumentProfileContext);

  const tableHeaders = [
    { key: 'documentType', label: 'Tipo Documento' },
    { key: 'siiDescription', label: 'Descripción SII' },
    { key: 'businessIdentifier', label: 'Rut Cliente' },
    { key: 'businessName', label: 'Cliente' },
    { key: 'stakeholderIdentifier', label: 'Rut Deudor' },
    { key: 'stakeholderName', label: 'Deudor' },
    {
      key: 'folio',
      label: 'Folio',
      handleAction: onOpenDocumentProfile,
      icon: <Info fontSize="small" />,
    },
    { key: 'amount', label: 'Monto', type: 'money' },
    { key: 'amountConciliated', label: 'Conciliado', type: 'money' },
    { key: 'creditNoteAmount', label: 'Monto nota crédito', type: 'money' },
    {
      key: 'leftAmountNet',
      label: 'Adeudado Doc.',
      type: 'invoice-remaining-amount',
    },
    {
      key: 'issuedDate',
      label: 'Fecha Emisión',
      type: 'date',
    },
    {
      key: 'receiptDate',
      label: 'Fecha Recepción',
      type: 'eight-days-date',
    },
    { key: 'paymentDate', label: 'Fecha vencimiento', type: 'date' },
    {
      key: 'originalPaymentDate',
      label: 'Fecha Vencimiento Original',
      type: 'date',
    },
    { key: 'arrearDays', label: 'Días Mora' },
    { key: 'lastCollectionManagementStatus', label: 'E. Cobranza' },
    { key: 'lastCollectionManagementOption', label: 'S. Cobranza' },
    {
      key: 'lastCollectionManagementObservation',
      label: 'O. Cobranza',
    },
    { key: 'commercialExecutiveName', label: 'Ejecutivo Comercial' },
    { key: 'collectorName', label: 'Ejecutivo Cobranza' },
    { key: 'fundName', label: 'Fondo' },
  ];

  const sectionedHeaders = [
    { key: 'documentType', label: 'Tipo Documento' },
    { key: 'stakeholderIdentifier', label: 'Rut Deudor' },
    { key: 'stakeholderName', label: 'Deudor' },
    {
      key: 'folio',
      label: 'Folio',
      handleAction: onOpenDocumentProfile,
      icon: <Info fontSize="small" />,
    },
    { key: 'amount', label: 'Monto', type: 'money' },
    { key: 'creditNoteAmount', label: 'Monto nota crédito', type: 'money' },
    {
      key: 'leftAmountNet',
      label: 'Adeudado Doc.',
      type: 'invoice-remaining-amount',
    },
    { key: 'paymentDate', label: 'Fecha vencimiento', type: 'date' },
    { key: 'arrearAmount', label: 'Interés Mora', type: 'money' },
    { key: 'arrearDays', label: 'Días Mora' },
    {
      key: 'lastCollectionManagementObservation',
      label: 'O. Cobranza',
    },
    { key: 'fundName', label: 'Fondo' },
    { key: 'siiDescription', label: 'Descripción SII' },
  ];

  const fetchData = async () => {
    const response = await getArrearsComiteeReport(dispatch);
    if (!response) throw new Error('Error al obtener los datos');
    const documents = [...response.documents];
    setData(documents);
    setCollectionStatesOptions(response.collectionStatesOptions);
    setCollectionOptionOptions(response.collectionOptionOptions);
    setCollectorOptions(response.collectorOptions);
    setCommercialExecutiveOptions(response.commercialExecutiveOptions);
    setBusinessNameOptions(response.businessNameOptions);
    setStakeholderNameOptions(response.stakeholderNameOptions);
    setArrearTrenchOptions(response.arrearTrenchOptions);
    return response;
  };

  const filteredData = useMemo(() => {
    return data.filter((document) => {
      const matchStatus =
        selectedStatus === 'all' ||
        (selectedStatus === 'En mora' && document.arrearDays > 0) ||
        (selectedStatus === 'Vigente' && document.arrearDays === 0);
      const matchArrearTrench =
        selectedArrearTrench === 'all' ||
        selectedArrearTrench === document.arrearTrench;
      const matchCollectionState =
        selectedCollectionState === 'all' ||
        document.lastCollectionManagementStatus === selectedCollectionState;
      const matchCollectionOption =
        selectedCollectionOption === 'all' ||
        document.lastCollectionManagementOption === selectedCollectionOption;
      const matchBusiness =
        selectedBusiness === 'all' ||
        selectedBusiness === document.businessName;
      const matchStakeholder =
        selectedStakeholder === 'all' ||
        selectedStakeholder === document.stakeholderName;
      const matchCollector =
        selectedCollector === 'all' ||
        selectedCollector === document.collectorName;
      const matchCommercialExecutive =
        selectedCommercialExecutive === 'all' ||
        selectedCommercialExecutive === document.commercialExecutiveName;
      const matchFolio = !folio.trim() || document.folio === folio.trim();
      const matchBusinessIdentifier =
        !businessIdentifier.trim() ||
        document.businessIdentifier === businessIdentifier.trim();
      const matchStakeholderIdentifier =
        !stakeholderIdentifier.trim() ||
        document.stakeholderIdentifier === stakeholderIdentifier.trim();
      return (
        matchStatus &&
        matchArrearTrench &&
        matchCollectionState &&
        matchCollectionOption &&
        matchBusiness &&
        matchStakeholder &&
        matchCollector &&
        matchCommercialExecutive &&
        matchFolio &&
        matchBusinessIdentifier &&
        matchStakeholderIdentifier
      );
    });
  }, [
    data,
    selectedStatus,
    selectedArrearTrench,
    selectedCollectionState,
    selectedCollectionOption,
    selectedBusiness,
    selectedStakeholder,
    selectedCollector,
    selectedCommercialExecutive,
    folio,
    businessIdentifier,
    stakeholderIdentifier,
  ]);

  const sectionedDataComputed = useMemo(
    () => groupByBusinessName(filteredData),
    [filteredData],
  );

  const setUp = async () => {
    setLoading(true);
    try {
      await fetchData();
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Error al obtener los datos');
    }
    setLoading(false);
  };

  useEffect(() => {
    void setUp();
  }, []);

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        variant="body1"
        title="Acá encontrarás todos los documentos activos para comité de mora."
        buttons={[
          {
            name: 'CustomToggleButtonGroup',
            action: () => {},
            color: 'primary',
            customButton: (
              <CustomToggleButtonGroup
                options={[
                  { label: 'Tabla', value: 'tabla' },
                  { label: 'Seccionado', value: 'seccionado' },
                ]}
                value={showSectioned ? 'seccionado' : 'tabla'}
                onChange={(value) => setShowSectioned(value === 'seccionado')}
              />
            ),
          },
        ]}
      />

      <Filters
        selectedStatus={selectedStatus}
        selectedArrearTrench={selectedArrearTrench}
        selectedCollectionState={selectedCollectionState}
        selectedCollectionOption={selectedCollectionOption}
        selectedBusiness={selectedBusiness}
        selectedStakeholder={selectedStakeholder}
        selectedCollector={selectedCollector}
        selectedCommercialExecutive={selectedCommercialExecutive}
        folio={folio}
        businessIdentifier={businessIdentifier}
        stakeholderIdentifier={stakeholderIdentifier}
        setSelectedStatus={setSelectedStatus}
        setSelectedArrearTrench={setSelectedArrearTrench}
        setSelectedCollectionState={setSelectedCollectionState}
        setSelectedCollectionOption={setSelectedCollectionOption}
        setSelectedBusiness={setSelectedBusiness}
        setSelectedStakeholder={setSelectedStakeholder}
        setSelectedCollector={setSelectedCollector}
        setSelectedCommercialExecutive={setSelectedCommercialExecutive}
        setFolio={setFolio}
        setBusinessIdentifier={setBusinessIdentifier}
        setStakeholderIdentifier={setStakeholderIdentifier}
        collectionStatesOptions={collectionStatesOptions}
        collectionOptionOptions={collectionOptionOptions}
        collectorOptions={collectorOptions}
        commercialExecutiveOptions={commercialExecutiveOptions}
        businessNameOptions={businessNameOptions}
        stakeholderNameOptions={stakeholderNameOptions}
        arrearTrenchOptions={arrearTrenchOptions}
      />

      {!showSectioned && (
        <EnhancedTable
          dataHeaders={tableHeaders}
          data={filteredData}
          rowsPerPageDefault={25}
          lastElement
          exportToFile="xlsx"
        />
      )}
      {showSectioned && (
        <div>
          {Object.entries(sectionedDataComputed).map(
            ([businessName, documents], index, array) => (
              <div key={businessName} style={{ marginBottom: '5px' }}>
                <Typography variant="h6" fontWeight="bold">
                  {businessName}
                </Typography>
                <EnhancedTable
                  dataHeaders={sectionedHeaders}
                  data={documents}
                  rowsPerPageDefault={5}
                  lastElement={index === array.length - 1}
                  exportToFile="xlsx"
                />
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
};

export default ArrearsComitee;
